import { type Component, createEffect, createMemo, createSignal, For, mergeProps, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import { useFieldError } from '~/hooks/useFieldError'
import type { RatingColorType, RatingPrecisionType, RatingPropsType, RatingSizeType } from '~/types/Rating'
import FieldMessage from './FieldMessage'
import { useFormContext } from './Form'

const iconSizes: Record<RatingSizeType, number> = { xs: 18, sm: 20, md: 22, lg: 26, xl: 32 }
const gapClasses: Record<RatingSizeType, string> = { xs: 'gap-0.5', sm: 'gap-1', md: 'gap-1', lg: 'gap-1.5', xl: 'gap-2' }

/** Estrellas llenas: color sólido del tema. 'default' y 'warning' son ámbar (semántica de estrellas). */
const colorClasses: Record<RatingColorType, string> = {
  primary: 'text-primary-500',
  secondary: 'text-slate-500',
  success: 'text-green-500',
  danger: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
  default: 'text-amber-500'
}

/**
 * Calificación por estrellas con soporte de medias estrellas (precision 0.5),
 * contador de valor, modo solo lectura y colores/tamaños del tema. El valor
 * viaja en un input oculto con `name` para integrarse con `<Form>`/`useForm`,
 * igual que Switch y Select.
 */
const Rating: Component<RatingPropsType> = props => {
  const merged = mergeProps(
    {
      max: 5,
      precision: 1 as RatingPrecisionType,
      size: 'md' as RatingSizeType,
      color: 'warning' as RatingColorType,
      readOnly: false,
      disabled: false,
      icon: 'material-symbols-light:star',
      emptyIcon: 'material-symbols-light:star-outline',
      showValue: false
    },
    props
  )

  const [local] = splitProps(merged, [
    'class',
    'max',
    'precision',
    'size',
    'color',
    'readOnly',
    'disabled',
    'icon',
    'emptyIcon',
    'showValue',
    'name',
    'error',
    'helperText',
    'value',
    'defaultValue',
    'onChange',
    'aria-label'
  ])

  const [value, setValue] = createSignal<number>(local.defaultValue ?? 0)
  const [preview, setPreview] = createSignal<number | null>(null)

  createEffect(() => {
    if (props.value !== undefined) setValue(props.value)
  })

  const currentValue = () => (local.value !== undefined ? local.value : value())

  /** Valor mostrado: la vista previa del hover mientras exista. */
  const displayValue = () => preview() ?? currentValue()

  const interactive = () => !local.readOnly && !local.disabled

  /** Valor de la estrella según la posición del puntero (mitad izquierda/derecha). */
  const valueFromEvent = (e: MouseEvent, starValue: number): number => {
    if (local.precision === 1) return starValue
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    return e.clientX < rect.left + rect.width / 2 ? starValue - 0.5 : starValue
  }

  const handleMove = (e: MouseEvent, starValue: number) => {
    if (!interactive()) return
    setPreview(valueFromEvent(e, starValue))
  }

  const handleLeave = () => setPreview(null)

  const handleClick = (e: MouseEvent, starValue: number) => {
    if (!interactive()) return
    const newValue = valueFromEvent(e, starValue)
    setValue(newValue)
    local.onChange?.(newValue)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!interactive()) return
    const delta = local.precision
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(local.max, currentValue() + delta)
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(0, currentValue() - delta)
    if (e.key === 'Home') next = 0
    if (e.key === 'End') next = local.max
    if (next === null) return
    e.preventDefault()
    setValue(next)
    local.onChange?.(next)
  }

  // Emitir change del input oculto solo cuando el valor cambia de verdad
  // (el patrón de Select: no realimentar al formulario que fija `value`).
  let hiddenRef: HTMLInputElement | undefined
  let lastEmitted: string | null = null
  createEffect(() => {
    if (!local.name || !hiddenRef) return
    const current = String(currentValue())
    if (current === lastEmitted) return
    lastEmitted = current
    // Los dos: el `<Form>` escucha `input` para revalidar en vivo y `change`
    // para el resto. Con sólo uno, la validación se quedaba congelada.
    hiddenRef.dispatchEvent(new Event('input', { bubbles: true }))
    hiddenRef.dispatchEvent(new Event('change', { bubbles: true }))
  })

  /**
   * El valor vive en una señal y el input oculto sólo lo refleja, así que el
   * `<Form>` no puede escribirlo por el DOM: al siguiente render se pierde.
   */
  const formCtx = useFormContext()
  const resolvedError = useFieldError(
    () => local.name,
    () => local.error
  )

  onMount(() => {
    const name = local.name
    if (!name || !formCtx) return

    formCtx.registerField(name, {
      getValue: () => currentValue(),
      setValue: next => {
        const parsed = Number(next)
        if (!Number.isNaN(parsed)) setValue(parsed)
      }
    })

    onCleanup(() => formCtx.unregisterField(name))
  })

  const starClass = twMerge(colorClasses[local.color])

  return (
    // La fila de estrellas es un `inline-flex`, así que el mensaje metido dentro
    // se colocaba a su derecha en vez de debajo.
    <div class="inline-flex flex-col">
      <div
        role="slider"
        tabIndex={interactive() ? 0 : -1}
        aria-valuemin={0}
        aria-valuemax={local.max}
        aria-valuenow={displayValue()}
        aria-valuetext={`${displayValue()} de ${local.max}`}
        aria-label={local['aria-label']}
        aria-readonly={local.readOnly || undefined}
        aria-disabled={local.disabled || undefined}
        onKeyDown={handleKeyDown}
        onBlur={() => setPreview(null)}
        class={twMerge(
          'inline-flex items-center outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-lg',
          gapClasses[local.size],
          local.disabled ? 'opacity-50' : '',
          interactive() ? 'cursor-pointer' : 'cursor-default',
          local.class
        )}
      >
        <For each={Array.from({ length: local.max })}>
          {(_, i) => {
            const starValue = i() + 1
            const filled = createMemo(() => Math.min(displayValue(), starValue))
            const isFull = createMemo(() => filled() >= starValue)
            const isHalf = createMemo(() => filled() > starValue - 1 && !isFull())
            return (
              <span class="relative inline-flex items-center justify-center" onMouseMove={e => handleMove(e, starValue)} onMouseLeave={handleLeave} onClick={e => handleClick(e, starValue)}>
                <Show when={!isFull()}>
                  <Icon
                    icon={local.emptyIcon}
                    size={iconSizes[local.size]}
                    class={twMerge('pointer-events-none text-slate-300 dark:text-slate-600', displayValue() === 0 && interactive() ? 'opacity-40' : '')}
                  />
                </Show>
                <Show when={isHalf()}>
                  <Icon icon={local.icon} size={iconSizes[local.size]} class={twMerge('pointer-events-none absolute', starClass)} style={{ 'clip-path': 'inset(0 50% 0 0)' }} />
                </Show>
                <Show when={isFull()}>
                  <Icon icon={local.icon} size={iconSizes[local.size]} class={twMerge('pointer-events-none', starClass)} />
                </Show>
              </span>
            )
          }}
        </For>
        <Show when={local.showValue}>
          <span class="ml-1 text-sm font-medium tabular-nums text-foreground-subtle select-none">
            {displayValue()}/{local.max}
          </span>
        </Show>
      </div>

      <Show when={local.name}>
        <input ref={hiddenRef} type="hidden" name={local.name} value={String(currentValue())} />
      </Show>

      <FieldMessage size={local.size} error={resolvedError()} helperText={local.helperText} indent={false} />
    </div>
  )
}

export default Rating
